import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useComparisons, useDeleteComparison } from "@/hooks/use-comparisons";
import { TrendingUp, Loader2, ChevronDown, ChevronRight, Plus, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatCurrency } from "@/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useState } from "react";
import { ComparisonModal } from "./ComparisonModal";
import { format } from "date-fns";
import { useLocation } from "wouter";
import { useComparisonMatchingSSE } from "@/hooks/useSSEProgress";
import { cn } from "@/lib/utils";
import { ConfirmDeleteDialog } from "@/components/ui/confirm-delete-dialog";
import { usePaymentMethodGate } from "@/hooks/use-subscription-guard";
import { PaywallModal } from "@/components/subscription/PaywallModal";

interface ComparisonsModuleProps {
    claimId: number;
    isOpen: boolean;
    onToggle: () => void;
    canWrite: boolean;
}

export function ComparisonsModule({ claimId, isOpen, onToggle, canWrite }: ComparisonsModuleProps) {
    const { comparisons, isLoading } = useComparisons(claimId);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPaywallOpen, setIsPaywallOpen] = useState(false);
    const { shouldPaywall } = usePaymentMethodGate();

    const handleNewComparison = (e: import("react").MouseEvent) => {
        e.stopPropagation();
        if (!canWrite) return;
        // Pay-as-you-go: block only when we definitively know there's no payment
        // method on file (see usePaymentMethodGate). The backend still enforces.
        if (shouldPaywall) {
            setIsPaywallOpen(true);
            return;
        }
        setIsModalOpen(true);
    };

    return (
        <Card className="shadow-xs w-full overflow-hidden">
            <ComparisonModal
                claimId={claimId}
                open={isModalOpen}
                onOpenChange={setIsModalOpen}
            />
            <PaywallModal
                open={isPaywallOpen}
                onOpenChange={setIsPaywallOpen}
                feature="comparison"
            />

            <Collapsible open={isOpen} onOpenChange={onToggle}>
                <CollapsibleTrigger className="w-full">
                    <CardHeader className="py-3 px-4 hover:bg-muted/50 cursor-pointer transition-colors overflow-hidden">
                        <div className="flex items-center justify-between min-w-0 w-full">
                            <div className="flex items-center gap-2 min-w-0">
                                <TrendingUp className="h-4 w-4 shrink-0" />
                                <CardTitle className="text-sm font-medium truncate">Comparisons</CardTitle>
                                <Badge variant="secondary" className="text-xs h-5 px-1.5 min-w-[1.25rem] shrink-0 flex items-center justify-center">
                                    {comparisons?.length || 0}
                                </Badge>
                            </div>
                            {isOpen ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
                        </div>
                    </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <CardContent className="px-4 pb-4 pt-0 w-full overflow-hidden">
                        <div className="pb-2 border-b mb-1"></div>
                        {isLoading ? (
                            <div className="flex justify-center p-4"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
                        ) : comparisons.length === 0 ? (
                            <div className="text-center py-6 text-sm text-muted-foreground">
                                No comparisons yet
                            </div>
                        ) : (
                            <div className="space-y-2 pt-2 max-h-[300px] overflow-y-auto pr-1 overflow-x-hidden scrollbar-thin scrollbar-thumb-muted-foreground/20 hover:scrollbar-thumb-muted-foreground/40">
                                {comparisons.map((comp) => (
                                    <ComparisonListItem key={comp.id} comparison={comp} claimId={claimId} canWrite={canWrite} />
                                ))}
                            </div>
                        )}
                        <div className="pt-3 border-t mt-3">
                            <Button size="sm" className="w-full text-xs h-8" onClick={handleNewComparison} disabled={!canWrite}>
                                <Plus className="h-3 w-3 mr-1" /> New Comparison
                            </Button>
                        </div>
                    </CardContent>
                </CollapsibleContent>
            </Collapsible>
        </Card>
    );
}

function ComparisonListItem({ comparison: comp, claimId, canWrite }: { comparison: any, claimId: number; canWrite: boolean }) {
    const [, setLocation] = useLocation();
    const [deleteOpen, setDeleteOpen] = useState(false);
    const deleteComparison = useDeleteComparison();

    // Determine if we should connect to SSE
    const isCompleted = comp.status === 'completed' || comp.status === 'Completed';
    const isFailed = comp.status === 'failed' || comp.status === 'Failed';
    const isActive = !isCompleted && !isFailed;

    const { progress, isComplete } = useComparisonMatchingSSE(comp.id, isActive);

    // Calculate display values
    // Ignore -1 progress
    const validProgress = (progress?.progress !== undefined && progress.progress >= 0) ? progress.progress : undefined;
    const showProgress = isActive && validProgress !== undefined;
    const stageName = progress?.message || (progress?.stage ? progress.stage.replace(/_/g, ' ') : comp.status);
    const formattedStage = progress?.message ? stageName : stageName.replace(/\b\w/g, (c: string) => c.toUpperCase());

    return (
        <>
            <div
                className="p-3 bg-muted/30 rounded-md border text-xs hover:bg-muted/60 transition-colors cursor-pointer relative overflow-hidden"
                onClick={() => {
                    if (!deleteOpen) setLocation(`/claims/${claimId}/comparison/${comp.id}`);
                }}
            >
                {/* Progress bar background for active comparisons */}
                {showProgress && (
                    <div
                        className="absolute bottom-0 left-0 h-1 bg-blue-500/20 transition-all duration-500"
                        style={{ width: `${validProgress}%` }}
                    />
                )}

                <div className="flex justify-between items-start mb-1 relative z-10 min-w-0 w-full gap-2 text-xs">
                    <span className="font-medium truncate flex-1 min-w-0">{comp.name}</span>
                    <div className="flex items-center gap-1 shrink-0">
                        <span className="text-muted-foreground whitespace-nowrap">{format(new Date(comp.createdAt), "MMM d")}</span>
                            {canWrite && (
                                <button
                                    className="p-0.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                                    onClick={(e) => { e.stopPropagation(); setDeleteOpen(true); }}
                                    aria-label={`Delete comparison ${comp.name}`}
                                    title="Delete comparison"
                                >
                                    <Trash2 className="h-3 w-3" />
                                </button>
                            )}
                    </div>
                </div>
                <div className="flex justify-between items-center text-muted-foreground relative z-10 min-w-0 w-full text-[10px]">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className={cn(
                            "truncate min-w-0 flex-1",
                            isActive && "text-blue-600 font-medium",
                            isCompleted && "text-green-600 font-medium",
                            isFailed && "text-red-600 font-medium"
                        )}>
                            {isActive && !isComplete ? (
                                <span className="flex items-center gap-1.5 min-w-0">
                                    <span className="truncate">{formattedStage}</span>
                                    {showProgress && <span className="text-[10px] opacity-80 shrink-0">({Math.round(validProgress)}%)</span>}
                                </span>
                            ) : (
                                <span className="truncate">{comp.status}</span>
                            )}
                        </span>
                        {isActive && !isComplete && <Loader2 className="h-3 w-3 animate-spin text-blue-500 shrink-0" />}
                    </div>
                    {comp.summary && (
                        <span className={cn("shrink-0 ml-2", comp.summary.differenceCents !== 0 ? "text-foreground font-medium" : "")}>
                            {formatCurrency(comp.summary.differenceCents / 100)}
                        </span>
                    )}
                </div>
            </div>

            <ConfirmDeleteDialog
                open={deleteOpen}
                onOpenChange={setDeleteOpen}
                onConfirm={async () => { await deleteComparison.mutateAsync(comp.id); }}
                title="Delete Comparison"
                description={`Are you sure you want to delete "${comp.name}"? This will also remove all associated supplements and match data. This action cannot be undone.`}
            />
        </>
    );
}